import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import KontentClient from '../utils/kontentAi/kontentAiClient';
import {
  transformFooterDisclaimersData,
  transformMarketingTilesData,
  transformFaqsData,
  transformWhatsCoveredData,
  transformTooltipsData,
  transformCmsTags,
  transformBannerContent,
  transformGeneralModularContent,
  transformGeneralModularTooltip,
  transformGeneralTooltip
} from '../utils/transformContent';
import { FAQ_CODENAME } from '../constants/cmsCodeName';
import { SLICES } from '../constants/slices';

export const kontentApi = createApi({
  reducerPath: SLICES.KONTENT_AI,
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getDisclaimer: builder.query({
      async queryFn({ brandPartner, previewEnabled }) {
        try {
          const codename = `${brandPartner}_disclaimer`;
          const response = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.codename', codename)
            .toPromise();
          return { data: transformFooterDisclaimersData(response?.data?.items?.[0] ?? null) };
        } catch (error) {
          return { error };
        }
      }
    }),

    getMarketingTiles: builder.query({
      async queryFn({ brandPartner, mrecTile, previewEnabled }) {
        try {
          const collectionCodename = `${brandPartner}_content`;
          const collection = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.collection', collectionCodename)
            .toPromise();

          const tileCodeNames =
            collection?.data?.items?.flatMap((item) => item?.elements?.[mrecTile]?.value || []) || [];

          if (tileCodeNames.length === 0) return { data: { mrecTile, data: [] } };

          const tileResponses = await Promise.all(
            tileCodeNames.map((codename) =>
              KontentClient(previewEnabled)
                .items()
                .equalsFilter('system.codename', codename)
                .toPromise()
                .then((res) => res.data.items?.[0])
            )
          );

          const transformed = transformMarketingTilesData(tileResponses);
          return { data: { mrecTile, data: transformed } };
        } catch (error) {
          return { error };
        }
      }
    }),

    getFaqContent: builder.query({
      async queryFn({ brandPartner, faqCodeName, previewEnabled }) {
        try {
          const FAQ_PARENT_MAP = {
            [FAQ_CODENAME.VERIFY_EMAIL]: FAQ_CODENAME.VERIFICATION,
            [FAQ_CODENAME.VERIFY_SMS]: FAQ_CODENAME.VERIFICATION,

            [FAQ_CODENAME.MY_PETS]: FAQ_CODENAME.RENEWAL_HUB,
            [FAQ_CODENAME.ADJUST_POLICY]: FAQ_CODENAME.RENEWAL_HUB,
            [FAQ_CODENAME.CONFIRMATION]: FAQ_CODENAME.RENEWAL_HUB
          };
          const faqCategory = `${brandPartner}${faqCodeName}`;
          const collectionCodename = `${brandPartner}_content`;

          const response = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.collection', collectionCodename)
            .type('portal')
            .depthParameter(3)
            .toPromise();

          const item = response?.data?.items?.[0];
          const linkedItems = item?.elements?.[FAQ_CODENAME.SUB_PAGE]?.linkedItems || [];
          const parentCodename = FAQ_PARENT_MAP[faqCodeName];

          if (parentCodename) {
            const parentFaq = linkedItems.find(
              (li) =>
                li.system?.codename === `${brandPartner}${parentCodename}` || li.system?.codename === parentCodename
            );
            const nestedFaqs = parentFaq?.elements?.[FAQ_CODENAME.SUB_PAGE]?.linkedItems || [];
            const specificFaq = nestedFaqs.find(
              (li) => li.system?.codename === faqCategory || li.system?.codename === faqCodeName
            );
            if (specificFaq) {
              return { data: transformFaqsData(specificFaq) };
            }
            return { data: transformFaqsData(parentFaq) };
          }
          const faqsData = linkedItems.find((li) => li.system?.codename === faqCategory) || null;
          return { data: transformFaqsData(faqsData) };
        } catch (error) {
          return { error };
        }
      }
    }),

    getWhatIsCovered: builder.query({
      async queryFn({ planType, policyCover, previewEnabled }) {
        try {
          const singleResp = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.collection', 'what_s_covered')
            .type('whats_covered_document')
            .depthParameter(2)
            .toPromise();

          return { data: transformWhatsCoveredData(singleResp?.data?.items?.[0] ?? null, planType, policyCover) };
        } catch (error) {
          return { error };
        }
      }
    }),
    getWhatsCoveredGeneric: builder.query({
      async queryFn({ previewEnabled, element = [] }) {
        try {
          const response = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.collection', 'what_s_covered')
            .type('whats_covered_document')
            .depthParameter(2)
            .toPromise();
          const content = response?.data?.items?.[0]?.elements || {};
          return { data: transformGeneralModularTooltip(content, element) };
        } catch (error) {
          return { error };
        }
      }
    }),
    getTooltips: builder.query({
      async queryFn() {
        try {
          const response = await KontentClient().items().equalsFilter('system.collection', 'tool_tips').toPromise();
          return { data: transformTooltipsData(response?.data?.items ?? []) };
        } catch (error) {
          return { error };
        }
      }
    }),

    getCmsTags: builder.query({
      async queryFn({ brandPartner, previewEnabled }) {
        try {
          const collectionCodename = `${brandPartner}_content`;

          const response = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.collection', collectionCodename)
            .type('portal')
            .toPromise();

          const item = response?.data?.items?.[0];
          const linkedItems = item?.elements?.brand_partner_details?.linkedItems || [];

          return { data: transformCmsTags(linkedItems) };
        } catch (error) {
          return { error };
        }
      }
    }),
    getBannerContent: builder.query({
      async queryFn({ previewEnabled }) {
        try {
          const response = await KontentClient(previewEnabled).item('renewal_banner').toPromise();

          return { data: transformBannerContent(response) };
        } catch (error) {
          return { error };
        }
      }
    }),
    getAdjustmentPolicyContent: builder.query({
      async queryFn({ previewEnabled, element = [] }) {
        try {
          const response = await KontentClient(previewEnabled).item('adjustpolicy').depthParameter(2).toPromise();

          const content = response?.data?.item?.elements || {};
          return { data: transformGeneralModularContent(response, content, element) };
        } catch (error) {
          return { error };
        }
      }
    }),
    getConfirmationPolicyContent: builder.query({
      async queryFn({ previewEnabled, element = [] }) {
        try {
          const response = await KontentClient(previewEnabled).item('confirmation').depthParameter(2).toPromise();

          const content = response?.data?.item?.elements || {};
          return { data: transformGeneralModularContent(response, content, element) };
        } catch (error) {
          return { error };
        }
      }
    }),
    getGeneralTooltipContent: builder.query({
      async queryFn({ previewEnabled, element = [] }) {
        try {
          const response = await KontentClient(previewEnabled)
            .items()
            .equalsFilter('system.type', 'tool_tip')
            .depthParameter(2)
            .toPromise();

          const content = response?.data?.items || {};
          return { data: transformGeneralTooltip(content, element) };
        } catch (error) {
          return { error };
        }
      }
    })
  })
});

export const {
  useGetDisclaimerQuery,
  useGetMarketingTilesQuery,
  useGetFaqContentQuery,
  useGetWhatIsCoveredQuery,
  useGetWhatsCoveredGenericQuery,
  useGetTooltipsQuery,
  useGetCmsTagsQuery,
  useGetBannerContentQuery,
  useGetAdjustmentPolicyContentQuery,
  useGetConfirmationPolicyContentQuery,
  useGetGeneralTooltipContentQuery
} = kontentApi;
